import { useMemo } from 'react';
import type { UnifiedCampaign } from '@/types/unifiedCampaign';
import { 
  validateInboundCampaignActivation, 
  validatePhoneNumberAssignment,
  getPhoneNumberReservations,
  getVapiKeyFromCampaign,
  type ValidationResult 
} from '@/utils/campaignValidation';

export interface PhoneNumberValidationHook {
  validateActivation: (campaign: UnifiedCampaign) => ValidationResult;
  validateAssignment: (campaign: UnifiedCampaign, phoneNumber: string, vapiKeyName: string) => ValidationResult;
  getReservations: () => Map<string, UnifiedCampaign[]>;
  isPhoneNumberAvailableForCreation: (phoneNumber: string, vapiKeyName: string, excludeCampaignId?: string) => boolean;
  isPhoneNumberAvailableForActivation: (phoneNumber: string, vapiKeyName: string, excludeCampaignId?: string) => boolean;
  getActiveInboundOnPhone: (phoneNumber: string) => UnifiedCampaign | undefined;
}

/**
 * Hook for validating phone number assignments and campaign activations
 */
export function usePhoneNumberValidation(campaigns: UnifiedCampaign[]): PhoneNumberValidationHook {
  const reservations = useMemo(() => getPhoneNumberReservations(campaigns), [campaigns]);

  const validateActivation = (campaign: UnifiedCampaign): ValidationResult => {
    return validateInboundCampaignActivation(campaign, campaigns);
  };

  const validateAssignment = (
    campaign: UnifiedCampaign, 
    phoneNumber: string, 
    vapiKeyName: string
  ): ValidationResult => {
    return validatePhoneNumberAssignment(campaign, phoneNumber, vapiKeyName, campaigns);
  };

  const getReservations = (): Map<string, UnifiedCampaign[]> => {
    return reservations;
  };

  const isPhoneNumberAvailableForCreation = (
    phoneNumber: string, 
    vapiKeyName: string, 
    excludeCampaignId?: string
  ): boolean => {
    // For creation, only check for cross-VAPI key conflicts
    // Allow multiple campaigns with same phone + same VAPI key
    const crossVapiConflict = campaigns.find(campaign => 
      campaign.id !== excludeCampaignId &&
      campaign.phone_number === phoneNumber &&
      campaign.status !== 'cancelled' && 
      campaign.status !== 'completed' &&
      getVapiKeyFromCampaign(campaign) !== vapiKeyName
    );

    return !crossVapiConflict; // Available if no cross-VAPI conflict
  };

  const isPhoneNumberAvailableForActivation = (
    phoneNumber: string, 
    vapiKeyName: string, 
    excludeCampaignId?: string
  ): boolean => {
    // For activation, check both cross-VAPI conflicts AND active inbound exclusivity
    
    // Check for cross-VAPI key conflicts (different VAPI keys using same phone number)
    const crossVapiConflict = campaigns.find(campaign => 
      campaign.id !== excludeCampaignId &&
      campaign.phone_number === phoneNumber &&
      campaign.status !== 'cancelled' && 
      campaign.status !== 'completed' &&
      getVapiKeyFromCampaign(campaign) !== vapiKeyName
    );

    if (crossVapiConflict) {
      return false; // Blocked due to cross-VAPI conflict
    }

    // Check for active inbound campaign exclusivity on the same phone number
    // (regardless of VAPI key - only one active inbound per phone number allowed)
    const activeInboundConflict = campaigns.find(campaign =>
      campaign.id !== excludeCampaignId &&
      campaign.phone_number === phoneNumber &&
      campaign.campaign_type === 'inbound' &&
      campaign.status === 'active'
    );

    if (activeInboundConflict) {
      return false; // Blocked due to active inbound exclusivity
    }

    return true; // Available for activation
  };

  const getActiveInboundOnPhone = (phoneNumber: string): UnifiedCampaign | undefined => {
    return campaigns.find(campaign => 
      campaign.phone_number === phoneNumber &&
      campaign.campaign_type === 'inbound' &&
      campaign.status === 'active'
    );
  };

  return {
    validateActivation,
    validateAssignment,
    getReservations,
    isPhoneNumberAvailableForCreation,
    isPhoneNumberAvailableForActivation,
    getActiveInboundOnPhone
  };
}